import React, { useEffect, useState, useContext } from "react";
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel, useMediaQuery, useTheme
} from "@material-ui/core";

import Input from "@material-ui/core/Input";
import { observer } from "mobx-react";
import PageStoreContext from "./context";
import { useStores } from "../../../../hooks";
import { PageFormType } from "./store";
import { changeEventHandlerDecorator, localize, toLocalizedString } from "../../../../common/libs";
import {
    CreateTablesRequest,
    TablesCreateTableRequest,
    UpdateTableRequest
} from "../../../../services/tables/requestsResponses";
import { SnackbarType } from "../../../../stores/common";
import { TextField } from "../../../../common/components/libs";
import { Trans, useTranslation } from "react-i18next";
import LocalizedInput from "../../../../common/components/translation/LocalizedInput";


const TableForm = () => {
    const theme = useTheme();
    const isSm = useMediaQuery(theme.breakpoints.up('sm'));
    const { t } = useTranslation(["general", "menu"]);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [count, setCount] = useState(1);
    const [startFromId, setStartFromId] = useState(1);
    const [publicId, setPublicId] = useState(1);
    const [externalId, setExternalId] = useState<string | undefined>();
    const [validationPublicIdErr, setValidationPublicIdErr] = useState(false);
    const [validationStartFromIdErr, setValidationStartFromIdErr] = useState(false);
    const [multipleTables, setMultipleTables] = useState(false);
    const [isCreatingTableInProgress, setIsCreatingTableInProgress] = useState(false);

    const { authStore, generalStore } = useStores();
    const pageStore = useContext(PageStoreContext);
    const { currentTable, isFormOpen } = pageStore.forms[PageFormType.editAndCreateTable];
    const { isReady } = pageStore;


    useEffect(() => {
        if (currentTable) {
            setPublicId(currentTable.data.publicId);
            setExternalId(currentTable.externalId)
        } else if (!currentTable && multipleTables) {
            setTitle("");
            setDescription("");
            setCount(1);
            setStartFromId(pageStore.getMaxPublicId() + 1)

        } else if (!currentTable && !multipleTables) {
            setTitle("");
            setDescription("");
            setPublicId(pageStore.getMaxPublicId() + 1);
        }
    }, [currentTable, isFormOpen, multipleTables, pageStore]);

    useEffect(() => {
        if (isReady) setStartFromId(pageStore.getMaxPublicId() + 1);
    }, [isReady, pageStore, pageStore.getMaxPublicId])


    const setStartFromIdWrapper = (inputValue: string) => {

        const checkNumbers = (inputValue: string) => {
            return /^s?\d{1,3}$/.test(inputValue)
        }
        let digitValue = parseInt(inputValue);
        if (checkNumbers(inputValue)) {
            setStartFromId(digitValue);
            setValidationStartFromIdErr(false);
        } else if (inputValue === "") {
            setStartFromId(undefined);
            setValidationPublicIdErr(false);
        } else {
            setValidationStartFromIdErr(true)
        }
    }
    const setPublicIdWrapper = (inputValue: string) => {

        const checkNumbers = (inputValue: string) => {
            return /^s?\d{1,3}$/.test(inputValue)
        }
        let digitValue = parseInt(inputValue);
        if (checkNumbers(inputValue)) {
            setPublicId(digitValue);
            setValidationPublicIdErr(false);
        } else if (inputValue === "") {
            setPublicId(undefined);
            setValidationPublicIdErr(false);
        } else {
            setValidationPublicIdErr(true)
        }
    }


    const handleSubmit = async () => {

        if (currentTable) {
            setIsCreatingTableInProgress(true);
            const dataUpdateTable: UpdateTableRequest = {
                data: {
                    description: currentTable.data.description,
                    publicId: publicId,
                    seats: [],
                    title: currentTable.data.title,
                    view: {
                        position: {
                            x: 0,
                            y: 0,
                        },
                        size: {
                            height: 0,
                            width: 0,
                        }
                    },
                },
                id: currentTable.id,
                organizationBranchId: authStore.currentUser.organizationData.organizationBranchId,
            }

            if (externalId !== currentTable.externalId) {
                await pageStore.updateExternalId({
                    organizationBranchId: dataUpdateTable.organizationBranchId,
                    tableId: currentTable.id,
                    externalId
                })
            }

            const updateTable = await pageStore.updateTable(dataUpdateTable);

            if (!updateTable) {
                const params: ({ type: SnackbarType, title: string, description?: string }) = {
                    title: t("general:somethingWentWrong"),
                    type: SnackbarType.error,
                    description: t("general:tryAgain")
                }
                generalStore.showSnackbar(params);
            } else {
                setIsCreatingTableInProgress(false);
                generalStore.showSuccessSnackbar(t("general:successfullyEdited"));
                pageStore.closeForm(PageFormType.editAndCreateTable)
            }


        } else if (!currentTable && multipleTables) {
            setIsCreatingTableInProgress(true);
            const data: CreateTablesRequest = {
                count: count,
                organizationBranchId: authStore.currentUser.organizationData.organizationBranchId,
                startFromId: startFromId,
                tableDataTemplate: {
                    description: toLocalizedString(description),
                    publicId: startFromId,
                    seats: [],
                    title: toLocalizedString(title),
                    view: {
                        position: {
                            x: 0,
                            y: 0,
                        },
                        size: {
                            height: 0,
                            width: 0,
                        },
                    }
                }
            }
            const newTables = await pageStore.createTables(data);
            if (!newTables) {
                const params: ({ type: SnackbarType, title: string, description?: string }) = {
                    title: t("general:somethingWentWrong"),
                    type: SnackbarType.error,
                    description: t("general:tryAgain")
                }
                generalStore.showSnackbar(params);

            } else {
                setIsCreatingTableInProgress(false);
                generalStore.showSuccessSnackbar(t("menu:tables.tablesCreated"));
                pageStore.closeForm(PageFormType.editAndCreateTable)
            }
        } else {
            setIsCreatingTableInProgress(true);
            const data: TablesCreateTableRequest = {
                data: {
                    description: toLocalizedString(description),
                    publicId: publicId,
                    seats: [],
                    title: toLocalizedString(title),
                    view: {
                        position: {
                            x: 0,
                            y: 0,
                        },
                        size: {
                            height: 0,
                            width: 0,
                        },
                    }
                },
                organizationBranchId: authStore.currentUser.organizationData.organizationBranchId
            }
            const singleTable = await pageStore.createTable(data);
            if (!singleTable) {
                const params: ({ type: SnackbarType, title: string, description?: string }) = {
                    title: t("general:somethingWentWrong"),
                    type: SnackbarType.error,
                    description: t("general:tryAgain")
                }
                generalStore.showSnackbar(params);

            } else {
                setIsCreatingTableInProgress(false);
                generalStore.showSuccessSnackbar(t("menu:tables.tableCreated"));
                pageStore.closeForm(PageFormType.editAndCreateTable)
            }
        }

    };

    const getHelperPublicId = () => {
        let res = t("menu:tables.tableLess1000");
        if (validationPublicIdErr) return res;
        return res;
    }


    const getHelperStartFromId = () => {
        let res = t("menu:tables.numberFirstGeneratedTable");
        if (validationPublicIdErr) return res;
        return res;
    }


    let titleForm;
    if (currentTable) {
        titleForm = t("menu:tables.editingTable");
    } else {
        titleForm = t("menu:tables.tableCreation");
    }

    let titleButton;
    if (multipleTables) {
        titleButton = t("menu:tables.addOneTable");
    } else {
        titleButton = t("menu:tables.addTables");
    }

    return (
        <Dialog
            open={isFormOpen}
            onClose={() => {
                pageStore.closeForm(PageFormType.editAndCreateTable)
            }
            }
            aria-labelledby="form-dialog-title"
            fullWidth={true}
            maxWidth={"sm"}
            fullScreen={!isSm}
        >
            <DialogTitle id="form-dialog-title">
                {titleForm}
            </DialogTitle>
            <DialogContent>
                <form autoComplete="off">
                    {(!currentTable && multipleTables) &&
						<FormControl fullWidth margin={"normal"}>
							<InputLabel htmlFor="count">{t("menu:tables.tablesNumberToCreate")}</InputLabel>
							<Input
								value={count}
								onChange={changeEventHandlerDecorator(setCount)}
							/>
						</FormControl>
                    }
                    {(!currentTable && multipleTables) &&
						<FormControl fullWidth margin={"normal"}>
							<TextField
								label={t("menu:tables.firstTableNumber")}
								helperText={getHelperStartFromId()}
								error={validationStartFromIdErr}
								fullWidth={true}
								value={startFromId || ""}
								onChange={changeEventHandlerDecorator(setStartFromIdWrapper)}
							>
							</TextField>
						</FormControl>
                    }
                    {(currentTable || !multipleTables) && <>
						<FormControl fullWidth margin={"normal"}>
							<TextField
								label={t("menu:tables.tableNumber")}
								helperText={getHelperPublicId()}
								error={validationPublicIdErr}
								fullWidth={true}
								value={publicId || ""}
								onChange={changeEventHandlerDecorator(setPublicIdWrapper)}
							>
							</TextField>
						</FormControl>
					</>
                    }
                    {
                        currentTable && <FormControl fullWidth margin={"normal"}>
							<TextField
								label={t("menu:tables.externalId")}
								fullWidth={true}
								value={externalId || ""}
								onChange={changeEventHandlerDecorator(setExternalId)}
							>
							</TextField>
						</FormControl>
                    }
                    {currentTable && <LocalizedInput
                        label = {t("general:name")}
                        value={currentTable.data.title}
                        setNewValue ={pageStore.setNewTableName.bind(pageStore)}
                    />}

                    {currentTable && <LocalizedInput
                        label = {t("general:description")}
                        value={currentTable.data.description}
                        setNewValue ={pageStore.setNewTableDescription.bind(pageStore)}
                    />}

                    {!currentTable &&
						<Button
							color="primary"
							onClick={() => {
                                setMultipleTables(!multipleTables)
                            }}
						>
                            {titleButton}
						</Button>
                    }
                </form>
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={() => {
                        pageStore.closeForm(PageFormType.editAndCreateTable)
                    }}
                    color="default"
                >
                    {t("general:cancel")}
                </Button>
                {
                    isCreatingTableInProgress ?
                        <Button
                            color="primary"
                            variant="contained"
                            disabled
                        >
                            <CircularProgress
                                size={25}
                                color="secondary"
                            />

                        </Button> :
                        <Button
                            color="primary"
                            variant="contained"
                            onClick={handleSubmit}
                        >
                            {t("general:save")}
                        </Button>
                }


            </DialogActions>
        </Dialog>
    );
};

export default observer(TableForm);
